import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCpu, FiCode, FiArrowRight, FiExternalLink } from "react-icons/fi";
import { useLanguage } from "../contexts/LanguageContext";

const Tools = () => {
    const { t } = useLanguage();

    const tools = [
        {
            title: t("nav.minddeck"),
            description: t("tools.minddeck.desc"),
            detailHref: "/tools/minddeck",
            externalHref: "https://minddeck.mewseeds.com",
            icon: FiCpu,
            gradient: "from-purple-500 to-pink-500",
            bgGradient:
                "from-purple-50 to-pink-50 dark:from-purple-900/20 to-pink-900/20",
            features: [
                t("tools.minddeck.feature1"),
                t("tools.minddeck.feature2"),
                t("tools.minddeck.feature3"),
                t("tools.minddeck.feature4"),
            ],
        },
        {
            title: t("nav.jsonparser"),
            description: t("tools.jsonparser.desc"),
            detailHref: "/tools/json-parser",
            externalHref: "https://json.mewseeds.com",
            icon: FiCode,
            gradient: "from-blue-500 to-cyan-500",
            bgGradient:
                "from-blue-50 to-cyan-50 dark:from-blue-900/20 to-cyan-900/20",
            features: [
                t("tools.jsonparser.feature1"),
                t("tools.jsonparser.feature2"),
                t("tools.jsonparser.feature3"),
                t("tools.jsonparser.feature4"),
            ],
        },
    ];

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent"
                >
                    {t("nav.tools")}
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
                >
                    {t("tools.subtitle")}
                </motion.p>
            </div>

            {/* Tools Grid */}
            <div className="grid gap-8">
                {tools.map((tool, index) => (
                    <motion.div
                        key={tool.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className={`p-8 rounded-2xl bg-gradient-to-br ${tool.bgGradient} border border-gray-200 dark:border-gray-700`}
                    >
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
                            <div className="flex-1 space-y-4">
                                <div className="flex items-center space-x-4">
                                    <div
                                        className={`p-3 rounded-xl bg-gradient-to-r ${tool.gradient} text-white`}
                                    >
                                        <tool.icon className="w-6 h-6" />
                                    </div>
                                    <h2 className="text-2xl font-bold">
                                        {tool.title}
                                    </h2>
                                </div>

                                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed">
                                    {tool.description}
                                </p>

                                <div className="grid grid-cols-2 gap-2">
                                    {tool.features.map((feature) => (
                                        <div
                                            key={feature}
                                            className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400"
                                        >
                                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                                            <span>{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row lg:flex-col space-y-3 sm:space-y-0 sm:space-x-3 lg:space-x-0 lg:space-y-3">
                                <Link
                                    to={tool.detailHref}
                                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700"
                                >
                                    <span>{t("tools.detail")}</span>
                                    <FiArrowRight className="w-4 h-4" />
                                </Link>

                                <a
                                    href={tool.externalHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r ${tool.gradient} text-white rounded-xl font-medium hover:shadow-xl hover:scale-105 transition-all duration-300`}
                                >
                                    <span>{t("tools.useNow")}</span>
                                    <FiExternalLink className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Coming Soon Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="p-8 rounded-2xl bg-gradient-to-br from-purple-100 via-blue-100 to-cyan-100 dark:from-purple-900 dark:via-blue-900 dark:to-cyan-900 border border-purple-200 dark:border-purple-800 text-center"
            >
                <h3 className="text-xl font-semibold mb-4">
                    {t("tools.coming.title")}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {t("tools.coming.desc")}
                </p>
                <Link
                    to="/roadmap"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300"
                >
                    <span>{t("tools.roadmap")}</span>
                    <FiArrowRight className="w-4 h-4" />
                </Link>
            </motion.div>
        </div>
    );
};

export default Tools;
